import { Application } from "pixi.js";
import { AudioController } from "./audio";
import { loadTextures, TextureAtlas } from "./assets";
import { LabyrinthPrototype, LightingMode, RunEndStatus } from "./game";
import { formatTime, LeaderboardClient, LeaderboardEntry } from "./leaderboard";
import { LevelData, loadLevel, validateLevel } from "./level";
import balanceConfig from "./config/game-balance.json";
import "./style.css";

type RankedDifficultyKey = "easy" | "medium" | "hard";
type DifficultyKey = RankedDifficultyKey | "custom";
type MenuView = "main" | "difficulty" | "custom" | "options" | "controls" | "credits" | "records";
type LevelIndex = Partial<Record<DifficultyKey, Array<{ url: string; seed: number }>>>;
type ScoreSaveBlockReason = "cheatUrl" | "customMode" | null;
type JsonPrimitive = string | number | boolean | null;
interface JsonRecord {
  [key: string]: JsonPrimitive | JsonRecord;
}
type JsonValue = JsonPrimitive | JsonRecord;
type CustomSectionKey = "difficulty" | "map_generation" | "runtime";
type CustomRunConfig = {
  difficulty: JsonRecord;
  map_generation: JsonRecord;
  runtime: JsonRecord;
};
type CustomSettingRow = {
  section: CustomSectionKey;
  path: string;
  value: string | number | boolean;
};

declare global {
  interface Window {
    __labyrinthPrototype?: LabyrinthPrototype;
  }
}

const difficultyLabels: Record<DifficultyKey, string> = {
  easy: balanceConfig.difficulties.easy?.label ?? "Einfach",
  medium: balanceConfig.difficulties.medium?.label ?? "Mittel",
  hard: balanceConfig.difficulties.hard?.label ?? "Schwer",
  custom: balanceConfig.difficulties.custom?.label ?? "Custom",
};
const configuredRankedOrder = (
  Array.isArray(balanceConfig.ranked_difficulty_order) ? balanceConfig.ranked_difficulty_order : ["easy", "medium", "hard"]
)
  .map((value) => String(value).toLowerCase())
  .filter(isRankedDifficultyKey);
const allDifficulties: RankedDifficultyKey[] = configuredRankedOrder.length ? configuredRankedOrder : ["easy", "medium", "hard"];
const configuredDefaultDifficulty: RankedDifficultyKey = isRankedDifficultyKey(balanceConfig.default_difficulty)
  ? balanceConfig.default_difficulty
  : "medium";
const pendingScoreId = "__pending__";
const customSeedStorageKey = "labyrinthCustomSeed";
const customPresetStorageKey = "labyrinthCustomPreset";
const customConfigStorageKey = "labyrinthCustomConfig";
const difficultyEditableKeys = [
  "num_regions",
  "player_speed",
  "light_drain",
  "fuel_spawn",
  "range_spawn",
  "coin_spawn",
  "sonar_spawn",
  "maze_openness",
] as const;

class WebGameShell {
  private readonly app: Application;
  private readonly textures: TextureAtlas;
  private readonly menuOverlay = requiredElement<HTMLDivElement>("#menu-overlay");
  private readonly launchScreen = requiredElement<HTMLElement>("#launch-screen");
  private readonly launchWelcome = requiredElement<HTMLElement>("#launch-welcome");
  private readonly launchTitle = requiredElement<HTMLElement>("#launch-title");
  private readonly menuPanel = requiredElement<HTMLElement>("#menu-panel");
  private readonly menuTitle = requiredElement<HTMLHeadingElement>("#menu-title");
  private readonly menuStatus = requiredElement<HTMLParagraphElement>("#menu-status");
  private readonly menuDetail = requiredElement<HTMLParagraphElement>("#menu-detail");
  private readonly mainPanel = requiredElement<HTMLDivElement>("#main-panel");
  private readonly difficultyPanel = requiredElement<HTMLDivElement>("#difficulty-panel");
  private readonly customPanel = requiredElement<HTMLDivElement>("#custom-panel");
  private readonly optionsPanel = requiredElement<HTMLDivElement>("#options-panel");
  private readonly controlsPanel = requiredElement<HTMLDivElement>("#controls-panel");
  private readonly creditsPanel = requiredElement<HTMLDivElement>("#credits-panel");
  private readonly recordsPanel = requiredElement<HTMLDivElement>("#records-panel");
  private readonly recordsText = requiredElement<HTMLParagraphElement>("#records-text");
  private readonly leaderboardForm = requiredElement<HTMLFormElement>("#leaderboard-submit");
  private readonly leaderboardTabs = requiredElement<HTMLDivElement>("#leaderboard-tabs");
  private readonly leaderboardList = requiredElement<HTMLDivElement>("#leaderboard-list");
  private readonly playerNameInput = requiredElement<HTMLInputElement>("#player-name");
  private readonly customSeedInput = requiredElement<HTMLInputElement>("#custom-seed");
  private readonly customPresetSelect = requiredElement<HTMLSelectElement>("#custom-preset");
  private readonly customSettingsContainer = requiredElement<HTMLDivElement>("#custom-settings");
  private readonly audioToggle = requiredElement<HTMLButtonElement>("#audio-toggle");
  private readonly debugHudToggle = requiredElement<HTMLButtonElement>("#debug-hud-toggle");
  private readonly cheatConsole = requiredElement<HTMLDivElement>("#cheat-console");
  private readonly cheatInput = requiredElement<HTMLInputElement>("#cheat-input");
  private readonly cheatSuggestions = requiredElement<HTMLDivElement>("#cheat-suggestions");
  private readonly actionButtons = [...document.querySelectorAll<HTMLButtonElement>("[data-action]")];
  private readonly startButtons = [...document.querySelectorAll<HTMLButtonElement>('[data-action="start"]')];
  private readonly resumeButtons = [...document.querySelectorAll<HTMLButtonElement>('[data-action="resume"]')];
  private readonly restartButtons = [...document.querySelectorAll<HTMLButtonElement>('[data-action="restart"]')];
  private readonly difficultyButtons = [...document.querySelectorAll<HTMLButtonElement>("[data-difficulty]")];
  private readonly menuViewButtons = [...document.querySelectorAll<HTMLButtonElement>("[data-menu-view]")];
  private readonly audio = new AudioController();
  private readonly leaderboard = new LeaderboardClient();
  private game: LabyrinthPrototype | null = null;
  private difficulty: RankedDifficultyKey = readInitialDifficulty();
  private menuView: MenuView = "main";
  private selectedMenuIndex = 0;
  private launchDone = false;
  private lastRunStatus: RunEndStatus | null = null;
  private pendingScore: { seed: number; difficulty: string; elapsedMs: number } | null = null;
  private highlightedScoreId: string | null = null;
  private readonly cheatUrlMode = new URLSearchParams(window.location.search).has("cheats");
  private readonly lightingMode = readLightingModeFromUrl();
  private cheatsEnabled = this.cheatUrlMode || localStorage.getItem("labyrinthCheats") === "on";
  private cheatWasPaused = false;
  private cheatSuggestionCommands: string[] = [];
  private selectedCheatSuggestion = -1;
  private recordsDifficulty: RankedDifficultyKey = this.difficulty;
  private scoreSaveBlockedReason: ScoreSaveBlockReason = null;
  private customSeed = readStoredCustomSeed();
  private customPreset: DifficultyKey = readStoredCustomPreset();
  private customConfig: CustomRunConfig = readStoredCustomConfig() ?? buildCustomConfigFromPreset(this.customPreset);
  private showDebugHud = localStorage.getItem("labyrinthDebugHud") === "on";

  constructor(app: Application, textures: TextureAtlas) {
    this.app = app;
    this.textures = textures;
    this.bindUi();
    this.applyDebugHudState();
    this.showMenu("Das Verrückte Labyrinth", "Bereit.");
    this.audio.playMusic("menu");
    this.playLaunchSequence();
  }

  private bindUi(): void {
    for (const button of this.actionButtons) {
      button.addEventListener("click", () => {
        const action = button.dataset.action;
        if (action === "start" || action === "restart") {
          this.audio.playSfx("menuSelect", 0.58);
          this.setMenuView("difficulty");
        } else if (action === "resume") {
          this.audio.playSfx("menuSelect", 0.58);
          this.resumeGame();
        } else if (action === "custom-start") {
          this.audio.playSfx("menuSelect", 0.58);
          void this.startCustomGame();
        } else if (action === "back") {
          this.audio.playSfx("menuMove", 0.5);
          if (this.menuView === "controls") {
            this.setMenuView("options");
          } else if (this.menuView === "credits") {
            this.setMenuView("main");
          } else if (this.menuView === "custom") {
            this.setMenuView("difficulty");
          } else {
            this.setMenuView("main");
          }
        } else if (action === "toggle-audio") {
          const enabled = this.audio.toggleEnabled();
          this.audioToggle.textContent = `Ton: ${enabled ? "An" : "Aus"}`;
        } else if (action === "toggle-debug-hud") {
          this.showDebugHud = !this.showDebugHud;
          this.applyDebugHudState();
        }
      });
    }
    for (const button of this.menuViewButtons) {
      button.addEventListener("click", () => {
        this.audio.playSfx("menuMove", 0.45);
        this.setMenuView(button.dataset.menuView as MenuView);
      });
    }
    for (const button of this.difficultyButtons) {
      button.addEventListener("click", () => {
        const selected = button.dataset.difficulty;
        if (selected === "custom") {
          this.setMenuView("custom");
          return;
        }
        if (!isRankedDifficultyKey(selected)) return;
        this.difficulty = selected;
        localStorage.setItem("labyrinthDifficulty", this.difficulty);
        this.updateDifficultyButtons();
        this.audio.playSfx("menuSelect", 0.58);
        void this.startGame();
      });
    }
    this.leaderboardForm.addEventListener("submit", (event) => {
      event.preventDefault();
      void this.submitPendingScore();
    });
    this.playerNameInput.addEventListener("input", () => {
      if (this.pendingScore && this.menuView === "records") {
        void this.refreshLeaderboard();
      }
    });
    this.customSeedInput.addEventListener("input", () => {
      this.customSeed = parseCustomSeed(this.customSeedInput.value, this.customSeed);
      localStorage.setItem(customSeedStorageKey, String(this.customSeed));
    });
    this.customPresetSelect.addEventListener("change", () => {
      const selected = this.customPresetSelect.value;
      if (!isDifficultyKey(selected)) return;
      this.customPreset = selected;
      localStorage.setItem(customPresetStorageKey, selected);
      this.resetCustomConfigToPreset(this.customPreset);
    });
    this.customSettingsContainer.addEventListener("input", (event) => this.handleCustomSettingsInput(event));
    this.customSettingsContainer.addEventListener("change", (event) => this.handleCustomSettingsInput(event));
    this.cheatInput.addEventListener("input", () => {
      this.selectedCheatSuggestion = -1;
      this.updateCheatSuggestions();
    });
    this.cheatInput.addEventListener("keydown", (event) => this.handleCheatKeyboard(event));
    this.menuPanel.addEventListener("pointermove", () => {
      this.menuPanel.classList.remove("is-keyboard-nav");
    });
    this.menuPanel.addEventListener("pointerdown", () => {
      this.menuPanel.classList.remove("is-keyboard-nav");
    });
    window.addEventListener("keydown", (event) => {
      if (isEditableTarget(event.target)) return;
      if (this.cheatsEnabled && !this.menuOverlay.hidden && event.code === "Backquote") {
        localStorage.setItem("labyrinthCheats", "on");
      }
      if (this.cheatsEnabled && this.menuOverlay.hidden && event.code === "KeyT") {
        this.openCheatConsole();
        event.preventDefault();
        return;
      }
      if (!this.menuOverlay.hidden) {
        this.handleMenuKeyboard(event);
        return;
      }
      if (event.code !== "Escape" || !this.game || this.lastRunStatus) return;
      if (this.game.isPaused()) {
        this.resumeGame();
      } else {
        this.pauseGame();
      }
    });
    this.updateDifficultyButtons();
    this.populateCustomPresetOptions();
    this.ensureCustomConfigLoaded();
    this.renderCustomSettings();
    this.customSeedInput.value = String(this.customSeed);
    this.customPresetSelect.value = this.customPreset;
    this.audioToggle.textContent = `Ton: ${this.audio.isEnabled() ? "An" : "Aus"}`;
    this.debugHudToggle.textContent = `Debug HUD: ${this.showDebugHud ? "An" : "Aus"}`;
    this.cheatConsole.hidden = true;
  }

  private async startGame(): Promise<void> {
    this.audio.clearTemporaryMusic();
    const seed = this.createRunSeed();
    this.showMenu("Lade Labyrinth ...", `${difficultyLabels[this.difficulty]} · Seed ${seed}`);
    const level = await this.loadRunLevel(this.difficulty, seed);
    this.replaceGame(level);
    this.scoreSaveBlockedReason = this.cheatUrlMode ? "cheatUrl" : null;
    this.lastRunStatus = null;
    this.pendingScore = null;
    this.highlightedScoreId = null;
    this.menuView = "main";
    this.menuOverlay.dataset.state = "playing";
    document.body.classList.add("is-playing");
    this.menuOverlay.hidden = true;
    this.menuPanel.hidden = false;
    this.launchScreen.hidden = true;
  }

  private async startCustomGame(): Promise<void> {
    this.audio.clearTemporaryMusic();
    const selectedPreset = this.customPresetSelect.value;
    if (!isDifficultyKey(selectedPreset)) return;
    this.customPreset = selectedPreset;
    localStorage.setItem(customPresetStorageKey, this.customPreset);
    this.customSeed = parseCustomSeed(this.customSeedInput.value, this.customSeed);
    this.customSeedInput.value = String(this.customSeed);
    localStorage.setItem(customSeedStorageKey, String(this.customSeed));
    this.customConfig = normalizeCustomConfigForPreset(this.customPreset, this.customConfig);
    this.persistCustomConfig();

    this.showMenu("Lade Labyrinth ...", `${difficultyLabels[this.customPreset]} · Seed ${this.customSeed}`);
    try {
      const level = await this.loadCustomRunLevel(this.customPreset, this.customSeed, this.customConfig);
      this.replaceGame(level);
      this.scoreSaveBlockedReason = this.cheatUrlMode ? "cheatUrl" : "customMode";
      this.lastRunStatus = null;
      this.pendingScore = null;
      this.highlightedScoreId = null;
      if (isRankedDifficultyKey(this.customPreset)) {
        this.recordsDifficulty = this.customPreset;
      }
      this.menuView = "main";
      this.menuOverlay.dataset.state = "playing";
      document.body.classList.add("is-playing");
      this.menuOverlay.hidden = true;
      this.menuPanel.hidden = false;
      this.launchScreen.hidden = true;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Custom-Level konnte nicht geladen werden.";
      this.setMenuView("custom");
      this.menuStatus.textContent = message;
      this.menuDetail.textContent = "Tipp: Mit Leaderboard-API kann jede Seed direkt generiert werden.";
    }
  }

  private replaceGame(level: LevelData): void {
    this.game?.destroy();
    this.audio.setRunSeed(level.seed);
    this.game = new LabyrinthPrototype(this.app, level, this.textures, {
      audio: this.audio,
      lightingMode: this.lightingMode,
      onRunEnded: (status, debugState) => this.handleRunEnded(status, debugState),
    });
    window.__labyrinthPrototype = this.game;
  }

  private async loadRunLevel(difficulty: DifficultyKey, exactSeed?: number): Promise<LevelData> {
    let generatedUnavailable = false;
    if (exactSeed !== undefined) {
      const generated = await this.tryLoadGeneratedLevel(difficulty, exactSeed);
      if (generated) return generated;
      generatedUnavailable = true;
    }
    const bySeed = await this.tryLoadIndexedLevel(difficulty, exactSeed);
    if (bySeed) {
      if (generatedUnavailable) {
        console.warn(
          `[level] Live-Level-API nicht erreichbar. Fallback-Level wird genutzt (${difficulty}, seed=${exactSeed ?? "n/a"}).`,
        );
      }
      return bySeed;
    }
    const randomVariant = await this.tryLoadIndexedLevel(difficulty);
    if (randomVariant) {
      if (generatedUnavailable) {
        console.warn(
          `[level] Live-Level-API nicht erreichbar. Zufallsfallback aus statischen Leveln wird genutzt (${difficulty}).`,
        );
      }
      return randomVariant;
    }
    if (generatedUnavailable) {
      console.warn(
        `[level] Live-Level-API nicht erreichbar. Nutze festes Basis-Level (${difficulty}).`,
      );
    }
    return loadLevel(`/levels/${difficulty}.json?run=${crypto.randomUUID()}`);
  }

  private async loadCustomRunLevel(preset: DifficultyKey, seed: number, customConfig: CustomRunConfig): Promise<LevelData> {
    const generated = await this.tryLoadGeneratedLevel(preset, seed, customConfig);
    if (generated) return generated;
    const fromIndex = await this.tryLoadIndexedLevel(preset, seed);
    if (fromIndex) {
      const customized = applyCustomConfigToLevel(fromIndex, customConfig);
      customized.seed = Math.max(1, Math.floor(seed));
      return customized;
    }
    const base = await this.tryLoadBaseDifficultyLevel(preset);
    if (base) {
      const customized = applyCustomConfigToLevel(base, customConfig);
      customized.seed = Math.max(1, Math.floor(seed));
      return customized;
    }
    throw new Error(`Custom-Level konnte nicht geladen werden (Seed ${seed}).`);
  }

  private async tryLoadIndexedLevel(difficulty: DifficultyKey, exactSeed?: number): Promise<LevelData | null> {
    try {
      const response = await fetch("/levels/index.json", { cache: "no-store" });
      if (!response.ok) throw new Error(`index failed: ${response.status}`);
      const index = (await response.json()) as LevelIndex;
      const variants = index[difficulty] ?? [];
      let selected = null;
      if (exactSeed !== undefined) {
        selected = variants.find((entry) => Number(entry.seed) === Number(exactSeed)) ?? null;
        if (!selected && variants.length > 0) {
          const indexBySeed = Math.abs(Math.floor(Number(exactSeed))) % variants.length;
          selected = variants[indexBySeed] ?? null;
        }
      } else {
        selected = variants[Math.floor(Math.random() * variants.length)] ?? null;
      }
      if (selected?.url) {
        return await loadLevel(`${selected.url}?run=${crypto.randomUUID()}`);
      }
    } catch {
      // Fallback for old builds without level index.
    }
    if (exactSeed !== undefined) return null;
    return null;
  }

  private async tryLoadGeneratedLevel(
    difficulty: DifficultyKey,
    seed: number,
    customConfig?: CustomRunConfig,
  ): Promise<LevelData | null> {
    for (const apiBase of this.levelApiCandidates()) {
      try {
        const response = await fetch(`${apiBase}/api/levels/custom`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            difficulty,
            seed,
            ...(customConfig ? { customConfig } : {}),
          }),
        });
        if (!response.ok) continue;
        const data = (await response.json()) as { level?: LevelData };
        if (!data.level) continue;
        validateLevel(data.level);
        return data.level;
      } catch {
        // Try next endpoint candidate.
      }
    }
    return null;
  }

  private levelApiCandidates(): string[] {
    const candidates: string[] = [];
    const configured = this.leaderboard.apiBaseUrl();
    if (configured) {
      candidates.push(configured.replace(/\/$/, ""));
    }
    const origin = window.location.origin;
    if (/^https?:\/\//.test(origin)) {
      const normalizedOrigin = origin.replace(/\/$/, "");
      if (!candidates.includes(normalizedOrigin)) {
        candidates.push(normalizedOrigin);
      }
      if (isLikelyLocalHost(window.location.hostname)) {
        const localApiCandidate = `${window.location.protocol}//${window.location.hostname}:8787`;
        if (!candidates.includes(localApiCandidate)) {
          candidates.push(localApiCandidate);
        }
      }
    }
    return candidates;
  }

  private async tryLoadBaseDifficultyLevel(preset: DifficultyKey): Promise<LevelData | null> {
    const candidates: DifficultyKey[] = [];
    if (preset === "custom") {
      candidates.push("custom", "medium", "easy", "hard");
    } else {
      candidates.push(preset, "medium", "easy", "hard");
    }
    const uniqueCandidates = Array.from(new Set(candidates));
    for (const difficulty of uniqueCandidates) {
      try {
        return await loadLevel(`/levels/${difficulty}.json?run=${crypto.randomUUID()}`);
      } catch {
        // Try next bundled fallback level.
      }
    }
    return null;
  }

  private createRunSeed(): number {
    try {
      const values = new Uint32Array(1);
      crypto.getRandomValues(values);
      return 1 + (values[0] % 2_147_483_646);
    } catch {
      return Math.floor(Math.random() * 2_147_483_646) + 1;
    }
  }

  private pauseGame(): void {
    if (!this.game) return;
    this.game.setPaused(true);
    this.audio.playTemporaryMusic("menu");
    this.menuView = "main";
    this.showMenu("Pausiert", "");
    this.menuOverlay.dataset.state = "paused";
    this.setRunButtons("paused");
  }

  private resumeGame(): void {
    if (!this.game) return;
    this.game.resume();
    this.audio.resumeFromTemporaryMusic();
    this.menuOverlay.hidden = true;
    this.menuOverlay.dataset.state = "playing";
    document.body.classList.add("is-playing");
  }

  private handleRunEnded(status: RunEndStatus, debugState: unknown): void {
    this.lastRunStatus = status;
    const title = status === "victory" ? "Ausgang erreicht" : "Dein Licht ist erloschen";
    const message = status === "victory" ? "" : "";
    const score = readScore(debugState);
    this.pendingScore = status === "victory" && score ? score : null;
    if (this.pendingScore) {
      this.highlightedScoreId = null;
      if (isRankedDifficultyKey(this.pendingScore.difficulty)) {
        this.recordsDifficulty = this.pendingScore.difficulty;
      }
    }
    this.playRunEndSequence(title, message, status);
    return;
  }

  private playRunEndSequence(title: string, message: string, status: RunEndStatus): void {
    this.menuTitle.textContent = title;
    this.menuStatus.textContent = message;
    this.menuOverlay.dataset.state = status;
    this.setRunButtons("ended");
    this.menuView = status === "victory" ? "records" : "main";
    this.audio.clearTemporaryMusic();
    this.audio.playMusic(status === "gameOver" ? "credits" : "menu");
    this.menuOverlay.hidden = false;
    this.launchScreen.hidden = false;
    this.menuPanel.hidden = true;
    this.launchWelcome.hidden = true;
    this.launchTitle.textContent = "";
    const fullTitle = title;
    let index = 0;
    const timer = window.setInterval(() => {
      index += 1;
      this.launchTitle.textContent = fullTitle.slice(0, index);
      if (index >= fullTitle.length) {
        window.clearInterval(timer);
        window.setTimeout(() => {
          this.launchScreen.hidden = true;
          this.menuPanel.hidden = false;
          this.setMenuView(this.menuView);
        }, 650);
      }
    }, 34);
  }

  private showMenu(title: string, status: string): void {
    this.menuTitle.textContent = title;
    this.menuStatus.textContent = status;
    this.setRunButtons("initial");
    this.menuOverlay.hidden = false;
    this.launchScreen.hidden = this.launchDone;
    this.menuPanel.hidden = !this.launchDone;
    this.setMenuView(this.menuView);
    this.updateDifficultyButtons();
    void this.refreshLeaderboard();
  }

  private setMenuView(view: MenuView): void {
    this.menuView = view;
    for (const button of this.menuViewButtons) {
      button.classList.toggle("is-selected", button.dataset.menuView === view);
    }
    this.mainPanel.hidden = view !== "main";
    this.difficultyPanel.hidden = view !== "difficulty";
    this.customPanel.hidden = view !== "custom";
    this.optionsPanel.hidden = view !== "options";
    this.controlsPanel.hidden = view !== "controls";
    this.creditsPanel.hidden = view !== "credits";
    this.recordsPanel.hidden = view !== "records";
    this.leaderboardForm.hidden = view !== "records" || !this.pendingScore || this.scoreSaveBlockedReason !== null;
    if (view === "difficulty") {
      this.menuStatus.textContent = "";
      this.menuDetail.textContent = "";
    } else if (view === "custom") {
      this.customSeedInput.value = String(this.customSeed);
      this.customPresetSelect.value = this.customPreset;
      this.renderCustomSettings();
      this.menuStatus.textContent = "";
      this.menuDetail.textContent = "";
    } else if (view === "options") {
      this.menuStatus.textContent = "";
      this.menuDetail.textContent = "";
    } else if (view === "controls") {
      this.menuStatus.textContent = "";
      this.menuDetail.textContent = "";
    } else if (view === "credits") {
      this.menuStatus.textContent = "";
      this.menuDetail.textContent = "";
    } else if (view === "records") {
      this.menuStatus.textContent = "";
      this.recordsText.textContent = this.pendingScore
        ? this.scoreSaveBlockedReason === "customMode"
          ? `Deine Zeit: ${formatTime(this.pendingScore.elapsedMs)} · Seed ${this.pendingScore.seed} · Custom-Modus (nicht ranked)`
          : this.scoreSaveBlockedReason === "cheatUrl"
            ? `Deine Zeit: ${formatTime(this.pendingScore.elapsedMs)} · Seed ${this.pendingScore.seed} · Cheats-URL (nicht ranked)`
            : `Deine Zeit: ${formatTime(this.pendingScore.elapsedMs)} · Seed ${this.pendingScore.seed}`
        : "";
      this.menuDetail.textContent = "";
      void this.refreshLeaderboard();
    } else {
      this.menuStatus.textContent = "";
      this.menuDetail.textContent = "";
    }
    this.selectedMenuIndex = 0;
    this.updateMenuSelection();
  }

  private updateDifficultyButtons(): void {
    for (const button of this.difficultyButtons) {
      button.classList.remove("is-current");
    }
  }

  private populateCustomPresetOptions(): void {
    const configured = (Array.isArray(balanceConfig.difficulty_order) ? balanceConfig.difficulty_order : [])
      .map((value) => String(value).toLowerCase())
      .filter(isDifficultyKey);
    const available = Object.keys(balanceConfig.difficulties ?? {})
      .map((value) => String(value).toLowerCase())
      .filter(isDifficultyKey);
    const baseOptions: DifficultyKey[] = configured.length ? configured : ["easy", "medium", "hard"];
    const options = Array.from(new Set<DifficultyKey>([...baseOptions, ...available, "custom"]));
    this.customPresetSelect.replaceChildren(...options.map((key) => {
      const option = document.createElement("option");
      option.value = key;
      option.textContent = difficultyLabels[key];
      return option;
    }));
    if (!options.includes(this.customPreset)) {
      this.customPreset = options.includes("custom") ? "custom" : (options[0] ?? "medium");
    }
    this.customPresetSelect.value = this.customPreset;
  }

  private ensureCustomConfigLoaded(): void {
    this.customConfig = normalizeCustomConfigForPreset(this.customPreset, this.customConfig);
    this.persistCustomConfig();
  }

  private resetCustomConfigToPreset(preset: DifficultyKey): void {
    this.customConfig = buildCustomConfigFromPreset(preset);
    this.persistCustomConfig();
    this.renderCustomSettings();
  }

  private persistCustomConfig(): void {
    localStorage.setItem(customConfigStorageKey, JSON.stringify(this.customConfig));
  }

  private renderCustomSettings(): void {
    const sections: Array<{ key: CustomSectionKey; title: string }> = [
      { key: "difficulty", title: "Difficulty" },
      { key: "map_generation", title: "Map Generation" },
      { key: "runtime", title: "Runtime" },
    ];
    const nodes: HTMLElement[] = [];
    for (const section of sections) {
      const sectionData = this.customConfig[section.key];
      const rows = flattenCustomSettings(section.key, sectionData);
      const wrap = document.createElement("section");
      wrap.className = "custom-settings-section";
      const title = document.createElement("h3");
      title.className = "custom-settings-title";
      title.textContent = section.title;
      wrap.appendChild(title);

      const table = document.createElement("table");
      table.className = "custom-settings-table";
      const head = document.createElement("thead");
      head.innerHTML = "<tr><th>Variable</th><th>Erklärung</th><th>Wert</th></tr>";
      table.appendChild(head);
      const body = document.createElement("tbody");
      for (const rowData of rows) {
        const row = document.createElement("tr");
        row.className = "custom-setting-row";

        const keyCell = document.createElement("td");
        keyCell.className = "custom-setting-key";
        const keyCode = document.createElement("code");
        keyCode.textContent = rowData.path;
        keyCell.appendChild(keyCode);
        row.appendChild(keyCell);

        const descCell = document.createElement("td");
        descCell.className = "custom-setting-description";
        descCell.textContent = customSettingDescription(rowData.section, rowData.path, rowData.value);
        row.appendChild(descCell);

        const valueCell = document.createElement("td");
        valueCell.className = "custom-setting-value";
        const input = this.createCustomSettingInput(rowData);
        valueCell.appendChild(input);
        row.appendChild(valueCell);

        body.appendChild(row);
      }
      table.appendChild(body);
      wrap.appendChild(table);
      nodes.push(wrap);
    }
    this.customSettingsContainer.replaceChildren(...nodes);
  }

  private createCustomSettingInput(rowData: CustomSettingRow): HTMLInputElement {
    const input = document.createElement("input");
    input.className = "custom-setting-input";
    input.dataset.section = rowData.section;
    input.dataset.path = rowData.path;
    if (typeof rowData.value === "boolean") {
      input.type = "checkbox";
      input.checked = rowData.value;
      return input;
    }
    if (typeof rowData.value === "number") {
      input.type = "number";
      input.step = numberStepForValue(rowData.value);
      input.value = String(rowData.value);
      return input;
    }
    input.type = "text";
    input.value = rowData.value;
    return input;
  }

  private handleCustomSettingsInput(event: Event): void {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    const section = input.dataset.section;
    const path = input.dataset.path;
    if (!isCustomSectionKey(section) || !path) return;

    let value: string | number | boolean;
    if (input.type === "checkbox") {
      value = input.checked;
    } else if (input.type === "number") {
      const parsed = Number(input.value);
      if (!Number.isFinite(parsed)) return;
      value = parsed;
    } else {
      value = input.value;
    }
    setNestedValue(this.customConfig[section], path.split("."), value);
    this.persistCustomConfig();
    if (this.customPreset !== "custom") {
      this.customPreset = "custom";
      this.customPresetSelect.value = "custom";
      localStorage.setItem(customPresetStorageKey, "custom");
    }
  }

  private applyDebugHudState(): void {
    document.body.classList.toggle("show-debug-hud", this.showDebugHud);
    localStorage.setItem("labyrinthDebugHud", this.showDebugHud ? "on" : "off");
    this.debugHudToggle.textContent = `Debug HUD: ${this.showDebugHud ? "An" : "Aus"}`;
  }

  private setRunButtons(state: "initial" | "paused" | "ended"): void {
    for (const button of this.startButtons) {
      button.hidden = state !== "initial";
    }
    for (const button of this.resumeButtons) {
      button.hidden = state !== "paused";
    }
    for (const button of this.restartButtons) {
      button.hidden = state === "initial";
    }
    this.updateMenuSelection();
  }

  private handleMenuKeyboard(event: KeyboardEvent): void {
    if (!this.launchDone) return;
    if (isEditableTarget(event.target)) return;
    const buttons = this.activeMenuButtons();
    if (event.code === "ArrowUp" || event.code === "KeyW") {
      this.menuPanel.classList.add("is-keyboard-nav");
      this.selectedMenuIndex = (this.selectedMenuIndex + buttons.length - 1) % buttons.length;
      this.updateMenuSelection();
      this.audio.playSfx("menuMove", 0.42);
      event.preventDefault();
      return;
    }
    if (event.code === "ArrowDown" || event.code === "KeyS") {
      this.menuPanel.classList.add("is-keyboard-nav");
      this.selectedMenuIndex = (this.selectedMenuIndex + 1) % buttons.length;
      this.updateMenuSelection();
      this.audio.playSfx("menuMove", 0.42);
      event.preventDefault();
      return;
    }
    if (event.code === "Enter" || event.code === "Space") {
      this.audio.playSfx("menuSelect", 0.5);
      buttons[this.selectedMenuIndex]?.click();
      event.preventDefault();
      return;
    }
    if (event.code === "Backspace" || event.code === "Escape") {
      if (this.menuView !== "main") {
        if (this.menuView === "controls") {
          this.setMenuView("options");
        } else if (this.menuView === "credits") {
          this.setMenuView("main");
        } else if (this.menuView === "custom") {
          this.setMenuView("difficulty");
        } else {
          this.setMenuView("main");
        }
        this.audio.playSfx("menuMove", 0.42);
      } else if (this.game && !this.lastRunStatus && this.game.isPaused()) {
        this.resumeGame();
      }
      event.preventDefault();
    }
  }

  private activeMenuButtons(): HTMLButtonElement[] {
    const buttons = [
      ...this.menuPanel.querySelectorAll<HTMLButtonElement>("button"),
    ].filter((button) => !button.hidden && !button.disabled && button.getClientRects().length > 0);
    return buttons.length ? buttons : [];
  }

  private updateMenuSelection(): void {
    const buttons = this.activeMenuButtons();
    if (!buttons.length) return;
    this.selectedMenuIndex = Math.max(0, Math.min(this.selectedMenuIndex, buttons.length - 1));
    buttons.forEach((button, index) => {
      button.classList.toggle("is-selected", index === this.selectedMenuIndex);
    });
    buttons[this.selectedMenuIndex]?.focus({ preventScroll: true });
  }

  private playLaunchSequence(): void {
    this.launchDone = false;
    this.launchScreen.hidden = false;
    this.menuPanel.hidden = true;
    this.launchWelcome.hidden = false;
    this.launchTitle.textContent = "";

    const title = "DaS vErRüCkTe LaByRiNtH";
    window.setTimeout(() => {
      this.audio.playSfx("gong", 0.48);
      let index = 0;
      const timer = window.setInterval(() => {
        index += 1;
        this.launchTitle.textContent = title.slice(0, index);
        if (index >= title.length) {
          window.clearInterval(timer);
          window.setTimeout(() => {
            this.launchDone = true;
            this.launchScreen.hidden = true;
            this.menuPanel.hidden = false;
            this.updateMenuSelection();
          }, 520);
        }
      }, 24);
    }, 700);
  }

  private async refreshLeaderboard(): Promise<void> {
    const boards = await Promise.all(
      allDifficulties.map(async (difficulty) => ({
        difficulty,
        entries: await this.leaderboard.listTop(5000, difficulty),
      })),
    );
    this.renderLeaderboardTabs();
    const byDifficulty = new Map(boards.map((board) => [board.difficulty, board.entries]));
    const entries = byDifficulty.get(this.recordsDifficulty) ?? [];
    this.leaderboardList.replaceChildren(this.renderLeaderboardTable(this.recordsDifficulty, entries));
  }

  private renderLeaderboardTabs(): void {
    this.leaderboardTabs.replaceChildren(
      ...allDifficulties.map((difficulty) => {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "leaderboard-tab";
        button.textContent = difficultyLabels[difficulty];
        button.classList.toggle("is-selected", this.recordsDifficulty === difficulty);
        button.addEventListener("click", () => {
          if (this.recordsDifficulty === difficulty) return;
          this.recordsDifficulty = difficulty;
          void this.refreshLeaderboard();
        });
        return button;
      }),
    );
  }

  private renderLeaderboardTable(difficulty: RankedDifficultyKey, sourceEntries: LeaderboardEntry[]): HTMLElement {
    const wrap = document.createElement("div");
    const table = document.createElement("table");
    table.className = "leaderboard-table";
    const head = document.createElement("thead");
    head.innerHTML = "<tr><th>Platz</th><th>Name</th><th>Zeit</th><th>Seed</th></tr>";
    table.appendChild(head);
    const body = document.createElement("tbody");
    table.appendChild(body);

    const entries = this.boardEntriesForDifficulty(difficulty, sourceEntries);
    if (!entries.length) {
      const row = document.createElement("tr");
      row.className = "is-empty";
      row.innerHTML = '<td colspan="4">Noch keine Zeiten gespeichert.</td>';
      body.appendChild(row);
      wrap.appendChild(table);
      return wrap;
    }

    const activeHighlight = this.pendingScore?.difficulty === difficulty ? pendingScoreId : this.highlightedScoreId;
    const highlightIndex = activeHighlight ? entries.findIndex((entry) => entry.id === activeHighlight) : -1;
    const visibleEntries = entries.slice(0, 10);

    visibleEntries.forEach((entry, index) => {
      this.appendLeaderboardRow(body, entry, index + 1, entry.id === activeHighlight);
    });

    if (highlightIndex >= 10 && highlightIndex < entries.length) {
      if (highlightIndex > 10) {
        const gapRow = document.createElement("tr");
        gapRow.className = "is-gap";
        gapRow.innerHTML = "<td>…</td><td>…</td><td>…</td><td>…</td>";
        body.appendChild(gapRow);
      }
      this.appendLeaderboardRow(body, entries[highlightIndex], highlightIndex + 1, true);
    }

    wrap.appendChild(table);
    return wrap;
  }

  private appendLeaderboardRow(body: HTMLTableSectionElement, entry: LeaderboardEntry, rank: number, highlight: boolean): void {
    const row = document.createElement("tr");
    row.classList.toggle("is-own-score", highlight);

    const rankCell = document.createElement("td");
    rankCell.textContent = String(rank);
    row.appendChild(rankCell);

    const nameCell = document.createElement("td");
    nameCell.textContent = entry.name || "…";
    row.appendChild(nameCell);

    const timeCell = document.createElement("td");
    timeCell.textContent = formatTime(entry.elapsedMs);
    row.appendChild(timeCell);

    const seedCell = document.createElement("td");
    seedCell.textContent = String(entry.seed);
    row.appendChild(seedCell);

    body.appendChild(row);
  }

  private boardEntriesForDifficulty(difficulty: RankedDifficultyKey, sourceEntries: LeaderboardEntry[]): LeaderboardEntry[] {
    const entries = [...sourceEntries].sort(scoreSort);
    const pending = this.pendingEntryForDifficulty(difficulty);
    if (pending) {
      entries.push(pending);
      entries.sort(scoreSort);
    }
    return entries;
  }

  private pendingEntryForDifficulty(difficulty: RankedDifficultyKey): LeaderboardEntry | null {
    if (!this.pendingScore || this.pendingScore.difficulty !== difficulty) return null;
    const name = this.playerNameInput.value.trim();
    return {
      id: pendingScoreId,
      name: name || "Anonym",
      seed: this.pendingScore.seed,
      difficulty: this.pendingScore.difficulty,
      elapsedMs: this.pendingScore.elapsedMs,
      createdAt: "9999-12-31T23:59:59.999Z",
    };
  }

  private async submitPendingScore(): Promise<void> {
    if (!this.pendingScore) return;
    if (this.scoreSaveBlockedReason !== null) {
      this.highlightedScoreId = null;
      this.pendingScore = null;
      this.leaderboardForm.hidden = true;
      this.recordsText.textContent = this.scoreSaveBlockedReason === "cheatUrl"
        ? "Cheat-URL aktiv: Zeit nicht gespeichert."
        : "Custom-Modus aktiv: Zeit nicht gespeichert.";
      this.playerNameInput.value = "";
      await this.refreshLeaderboard();
      return;
    }
    const score = this.pendingScore;
    const normalizedName = this.playerNameInput.value.trim() || "Anonym";
    let entry: LeaderboardEntry;
    try {
      entry = await this.leaderboard.submit({
        ...score,
        name: normalizedName,
      });
    } catch {
      this.recordsText.textContent = "Zeit konnte nicht gespeichert werden (Leaderboard-API nicht erreichbar).";
      this.leaderboardForm.hidden = false;
      await this.refreshLeaderboard();
      return;
    }
    this.highlightedScoreId = entry.id;
    this.pendingScore = null;
    this.leaderboardForm.hidden = true;
    this.recordsText.textContent = "Zeit gespeichert.";
    this.playerNameInput.value = "";
    await this.refreshLeaderboard();
  }

  private openCheatConsole(): void {
    if (!this.game || !this.cheatsEnabled) return;
    this.cheatWasPaused = this.game.isPaused();
    this.game.setPaused(true);
    this.cheatConsole.hidden = false;
    this.cheatInput.value = "/";
    this.selectedCheatSuggestion = -1;
    this.updateCheatSuggestions();
    this.cheatInput.focus({ preventScroll: true });
  }

  private closeCheatConsole(): void {
    this.cheatConsole.hidden = true;
    this.cheatSuggestionCommands = [];
    this.selectedCheatSuggestion = -1;
    if (this.game && !this.cheatWasPaused) {
      this.game.resume();
    }
  }

  private handleCheatKeyboard(event: KeyboardEvent): void {
    if (event.code === "Escape") {
      this.closeCheatConsole();
      event.preventDefault();
      return;
    }
    if (event.code === "ArrowDown" || event.code === "ArrowUp") {
      if (!this.cheatSuggestionCommands.length) return;
      const direction = event.code === "ArrowDown" ? 1 : -1;
      const nextIndex = (this.selectedCheatSuggestion + direction + this.cheatSuggestionCommands.length) % this.cheatSuggestionCommands.length;
      this.selectedCheatSuggestion = nextIndex;
      this.updateCheatSuggestions();
      this.scrollSelectedCheatSuggestionIntoView();
      event.preventDefault();
      return;
    }
    if (event.code === "Tab") {
      event.preventDefault();
      if (!this.cheatSuggestionCommands.length) return;
      this.applyCheatAutocomplete();
      return;
    }
    if (event.code === "Enter") {
      if (this.selectedCheatSuggestion >= 0) {
        this.applyCheatSuggestion(this.selectedCheatSuggestion);
      } else if (this.cheatSuggestionCommands.length === 1 && this.cheatSuggestionCommands[0] !== this.cheatInput.value.trim()) {
        this.applyCheatSuggestion(0);
      } else {
        const command = this.cheatInput.value.trim();
        this.runCheatCommand(command);
        this.closeCheatConsole();
      }
      event.preventDefault();
      return;
    }
  }

  private updateCheatSuggestions(): void {
    const input = this.cheatInput.value.toLowerCase().trimStart();
    const commands = this.cheatCommandTemplates();
    const filtered = commands.filter((command) => command.toLowerCase().startsWith(input || "/")).slice(0, 8);
    this.cheatSuggestionCommands = filtered;
    if (this.selectedCheatSuggestion >= filtered.length) {
      this.selectedCheatSuggestion = filtered.length - 1;
    }
    this.cheatSuggestions.replaceChildren(...filtered.map((command, index) => {
      const row = document.createElement("div");
      row.textContent = command;
      row.classList.toggle("is-selected", index === this.selectedCheatSuggestion);
      return row;
    }));
  }

  private cheatCommandTemplates(): string[] {
    return [
      "/tp region <nr>",
      "/tp dealer <nr>",
      "/tp start",
      "/tp end",
      "/give range <anzahl>",
      "/give sonar <anzahl>",
      "/give campfire <anzahl>",
      "/give pickaxe <anzahl>",
      "/give compass <anzahl>",
      "/give betterTank",
      "/give advancedView",
      "/give gemMagnet",
      "/give gems <anzahl>",
      "/give key <nr>",
      "/fuel <0-100>",
      "/freeze fuel on|off",
      "/noclip on|off",
      "/report",
    ];
  }

  private applyCheatSuggestion(index: number): void {
    const suggestion = this.cheatSuggestionCommands[index];
    if (!suggestion) return;
    this.cheatInput.value = suggestion;
    this.cheatInput.setSelectionRange(this.cheatInput.value.length, this.cheatInput.value.length);
    this.selectedCheatSuggestion = index;
    this.updateCheatSuggestions();
    this.scrollSelectedCheatSuggestionIntoView();
  }

  private applyCheatAutocomplete(): void {
    if (!this.cheatSuggestionCommands.length) return;
    const current = this.cheatInput.value.trim();
    if (this.cheatSuggestionCommands.length === 1) {
      this.cheatInput.value = this.cheatSuggestionCommands[0];
      this.cheatInput.setSelectionRange(this.cheatInput.value.length, this.cheatInput.value.length);
      this.selectedCheatSuggestion = 0;
      this.updateCheatSuggestions();
      return;
    }
    const prefix = longestCommonPrefix(this.cheatSuggestionCommands);
    if (prefix.length > current.length) {
      this.cheatInput.value = prefix;
      this.cheatInput.setSelectionRange(prefix.length, prefix.length);
      this.selectedCheatSuggestion = -1;
      this.updateCheatSuggestions();
    }
  }

  private scrollSelectedCheatSuggestionIntoView(): void {
    if (this.selectedCheatSuggestion < 0) return;
    const row = this.cheatSuggestions.children.item(this.selectedCheatSuggestion);
    if (row instanceof HTMLElement) {
      row.scrollIntoView({ block: "nearest" });
    }
  }

  private runCheatCommand(command: string): void {
    if (!this.game || !command.startsWith("/")) return;
    const tokens = command.slice(1).trim().split(/\s+/).filter(Boolean);
    const [rawAction = "", rawA = "", ...rest] = tokens;
    const action = rawAction.toLowerCase();
    const a = rawA.toLowerCase();
    const b = rest.join(" ").trim();
    if (action === "tp" && a === "region") {
      this.game.debugTeleportToRegion(Number(b));
    } else if (action === "tp" && a === "dealer") {
      this.game.debugTeleportToDealer(Number(b || 0));
    } else if (action === "tp" && a === "start") {
      this.game.debugTeleportToStart();
    } else if (action === "tp" && a === "end") {
      this.game.debugTeleportToEnd();
    } else if (action === "give") {
      if (a === "key") {
        this.game.debugGiveKey(Number(b || 1));
      } else {
        this.game.debugGiveItem(a, Number(b || 1));
      }
    } else if (action === "fuel") {
      const value = Number(rawA);
      this.game.debugSetFuel(value > 1 ? value / 100 : value);
    } else if (action === "freeze" && a === "fuel") {
      this.game.debugSetFuelFrozen(b !== "off");
    } else if (action === "noclip") {
      this.game.debugSetNoClip(a !== "off");
    } else if (action === "report") {
      console.table(this.game.debugLevelReport());
    }
  }
}

function readScore(debugState: unknown): { seed: number; difficulty: string; elapsedMs: number } | null {
  if (!debugState || typeof debugState !== "object") return null;
  const state = debugState as Record<string, unknown>;
  const seed = Number(state.seed);
  const difficulty = String(state.difficulty ?? "");
  const elapsedMs = Number(state.elapsedMs);
  if (!Number.isFinite(seed) || !difficulty || !Number.isFinite(elapsedMs) || elapsedMs <= 0) return null;
  return { seed, difficulty, elapsedMs };
}

async function main(): Promise<void> {
  const root = requiredElement<HTMLDivElement>("#game-root");
  const app = new Application();
  await app.init({
    background: "#111417",
    resizeTo: window,
    antialias: false,
    autoDensity: true,
    resolution: Math.min(window.devicePixelRatio || 1, 2),
    preference: "webgl",
  });
  root.appendChild(app.canvas);

  requiredElement<HTMLParagraphElement>("#menu-status").textContent = "Assets werden geladen ...";
  const textures = await loadTextures();
  new WebGameShell(app, textures);
}

function readInitialDifficulty(): RankedDifficultyKey {
  const saved = localStorage.getItem("labyrinthDifficulty");
  return isRankedDifficultyKey(saved) ? saved : configuredDefaultDifficulty;
}

function readLightingModeFromUrl(): LightingMode {
  const params = new URLSearchParams(window.location.search);
  const raw = (
    params.get("lighting") ??
    params.get("lightmode") ??
    (params.has("occlusion") ? "occlusion" : "")
  ).toLowerCase();
  if (raw === "occlusion" || raw === "experimental" || raw === "raytrace" || raw === "raytracing") {
    return "occlusion";
  }
  return "classic";
}

function scoreSort(a: LeaderboardEntry, b: LeaderboardEntry): number {
  return a.elapsedMs - b.elapsedMs || a.createdAt.localeCompare(b.createdAt);
}

function longestCommonPrefix(values: string[]): string {
  if (!values.length) return "";
  let prefix = values[0];
  for (let index = 1; index < values.length; index += 1) {
    const value = values[index];
    while (!value.startsWith(prefix) && prefix.length > 0) {
      prefix = prefix.slice(0, -1);
    }
    if (!prefix) break;
  }
  return prefix;
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    target.isContentEditable
  );
}

function requiredElement<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) {
    throw new Error(`Missing required element: ${selector}`);
  }
  return element;
}

function isDifficultyKey(value: unknown): value is DifficultyKey {
  return value === "easy" || value === "medium" || value === "hard" || value === "custom";
}

function isRankedDifficultyKey(value: unknown): value is RankedDifficultyKey {
  return value === "easy" || value === "medium" || value === "hard";
}

function isLikelyLocalHost(hostname: string): boolean {
  const lower = hostname.toLowerCase();
  return lower === "localhost" || lower === "127.0.0.1" || lower === "::1" || lower === "0.0.0.0";
}

function readStoredCustomSeed(): number {
  const stored = localStorage.getItem(customSeedStorageKey);
  return parseCustomSeed(stored, Math.floor(Math.random() * 900_000_000) + 1);
}

function readStoredCustomPreset(): DifficultyKey {
  const stored = localStorage.getItem(customPresetStorageKey);
  if (isDifficultyKey(stored)) return stored;
  return "custom";
}

function parseCustomSeed(value: string | null, fallback: number): number {
  const parsed = Number(value);
  const numeric = Number.isFinite(parsed) ? parsed : fallback;
  const clamped = Math.max(1, Math.min(2_147_483_647, Math.floor(numeric)));
  return clamped;
}

function readStoredCustomConfig(): CustomRunConfig | null {
  const stored = localStorage.getItem(customConfigStorageKey);
  if (!stored) return null;
  try {
    const parsed = JSON.parse(stored);
    if (!isPlainRecord(parsed)) return null;
    const candidate = parsed as Record<string, unknown>;
    const difficulty = normalizeToJsonRecord(candidate.difficulty);
    const mapGeneration = normalizeToJsonRecord(candidate.map_generation);
    const runtime = normalizeToJsonRecord(candidate.runtime);
    if (!Object.keys(difficulty).length || !Object.keys(runtime).length) {
      return null;
    }
    return {
      difficulty,
      map_generation: mapGeneration,
      runtime,
    };
  } catch {
    return null;
  }
}

function buildCustomConfigFromPreset(preset: DifficultyKey): CustomRunConfig {
  const presetSource = difficultySourceForKey(preset);
  const mergedDifficultySource = mergeJsonRecords(
    normalizeToJsonRecord(balanceConfig.difficulty_base),
    normalizeToJsonRecord(presetSource),
  );
  const difficulty: JsonRecord = {};
  for (const key of difficultyEditableKeys) {
    const value = mergedDifficultySource[key];
    const normalized = normalizeToJsonValue(value);
    if (normalized !== undefined) {
      difficulty[key] = normalized;
    }
  }
  const mapGeneration = mergeJsonRecords(
    normalizeToJsonRecord(balanceConfig.map_generation),
    normalizeToJsonRecord(presetSource.map_generation_overrides),
  );
  const runtime = mergeJsonRecords(
    normalizeToJsonRecord(balanceConfig.runtime),
    normalizeToJsonRecord(presetSource.runtime_overrides),
  );
  return {
    difficulty,
    map_generation: mapGeneration,
    runtime,
  };
}

function normalizeCustomConfigForPreset(preset: DifficultyKey, current: CustomRunConfig): CustomRunConfig {
  const base = buildCustomConfigFromPreset(preset);
  return {
    difficulty: mergeKnownShape(base.difficulty, current.difficulty),
    map_generation: mergeKnownShape(base.map_generation, current.map_generation),
    runtime: mergeKnownShape(base.runtime, current.runtime),
  };
}

function flattenCustomSettings(section: CustomSectionKey, data: JsonRecord): CustomSettingRow[] {
  const rows: CustomSettingRow[] = [];
  flattenCustomSettingRows(section, data, "", rows);
  return rows;
}

function flattenCustomSettingRows(section: CustomSectionKey, value: JsonValue, prefix: string, out: CustomSettingRow[]): void {
  if (isPlainRecord(value)) {
    for (const key of Object.keys(value).sort((a, b) => a.localeCompare(b))) {
      const child = value[key];
      const path = prefix ? `${prefix}.${key}` : key;
      flattenCustomSettingRows(section, child, path, out);
    }
    return;
  }
  if (typeof value === "number" || typeof value === "boolean" || typeof value === "string") {
    out.push({
      section,
      path: prefix,
      value,
    });
  }
}

function customSettingDescription(section: CustomSectionKey, path: string, value: string | number | boolean): string {
  if (section === "difficulty") {
    const descriptions: Record<string, string> = {
      num_regions: "Anzahl der Regionen/Knotenbereiche in der Karte.",
      player_speed: "Laufgeschwindigkeit des Spielers.",
      light_drain: "Wie schnell das Licht/Fuel leer wird.",
      fuel_spawn: "Grund-Spawnchance für Fuel-Items.",
      range_spawn: "Grund-Spawnchance für Range-Items.",
      coin_spawn: "Grund-Spawnchance für Gems/Coins.",
      sonar_spawn: "Grund-Spawnchance für Sonar-Items.",
      maze_openness: "Wie offen oder verwinkelt die Wege erzeugt werden.",
    };
    return descriptions[path] ?? "Grundwert für diese Schwierigkeit.";
  }

  if (section === "map_generation") {
    if (path === "tile_size") return "Pixelgröße eines einzelnen Tiles.";
    if (path === "base_width") return "Absolute Kartenbreite in Tiles.";
    if (path === "base_height") return "Absolute Kartenhöhe in Tiles.";
    if (path === "lamp_radius") return "Standard-Lichtradius von Lampen.";
    if (path.includes("region_spawns.")) {
      const regionMatch = path.match(/region_spawns\.([^.]+)/);
      const regionToken = regionMatch?.[1] ?? "default";
      const regionLabel = regionToken === "default" ? "alle Regionen" : regionToken.replace("region_", "Region ");
      if (path.endsWith(".lights.wall_lamp_chance")) return `Chance pro Wandtile auf Lampen-Spawn (${regionLabel}).`;
      if (path.endsWith(".lights.min_wall_lamps")) return `Mindestanzahl an Lampen pro Region (${regionLabel}).`;
      if (path.endsWith(".lights.lamp_radius")) return `Lampenradius-Override für ${regionLabel}.`;
      if (path.endsWith(".items.fuel")) return `Regionale Spawnchance für Fuel (${regionLabel}).`;
      if (path.endsWith(".items.range")) return `Regionale Spawnchance für Range (${regionLabel}).`;
      if (path.endsWith(".items.coin")) return `Regionale Spawnchance für Coins/Gems (${regionLabel}).`;
      if (path.endsWith(".items.sonar")) return `Regionale Spawnchance für Sonar (${regionLabel}).`;
      return `Regionales Spawn-Tuning für ${regionLabel}.`;
    }
    return "Parameter der Karten- und Spawn-Generierung.";
  }

  if (section === "runtime") {
    if (path.startsWith("durations_ms.")) return "Dauer eines zeitbasierten Effekts oder einer Animation (ms).";
    if (path.startsWith("camera.")) return "Kamera-Zoomverhalten bei aktiven Effekten.";
    if (path.startsWith("player_light.base_radius")) return "Maximale Sichtweite des Spielerlichts.";
    if (path.startsWith("player_light.advanced_view_radius")) return "Sichtweite mit Advanced-View-Upgrade.";
    if (path.startsWith("player_light.fuel_curve_exponent")) return "Kurvenform, wie stark Sicht mit Fuel abnimmt.";
    if (path.startsWith("player_light.core_ratio")) return "Optische Verteilung von hartem Kern zu weichem Schatten.";
    if (path.startsWith("pickup.default_distance_tiles")) return "Normale Aufsammelreichweite in Tiles.";
    if (path.startsWith("pickup.coin_magnet_distance_tiles")) return "Magnet-Reichweite für Gems.";
    if (path.startsWith("pickup.coin_magnet_pull_")) return "Stärke/Verhalten, wie stark Gems angezogen werden.";
    if (path.startsWith("pickup.fuel_amount")) return "Fuel-Menge pro Fuel-Item.";
    if (path.startsWith("refill.distance_tiles")) return "Reichweite der Refill-Zone.";
    if (path.startsWith("refill.rate_per_second")) return "Nachfüllrate pro Sekunde.";
    if (path.startsWith("refill.sound_delay_ms")) return "Verzögerung bis Refill-Sound startet.";
    if (path.startsWith("low_fuel_warning.threshold")) return "Schwelle, ab wann Low-Fuel-Warnung aktiv ist.";
    if (path.startsWith("low_fuel_warning.")) return "Lautstärke/Tempo der Low-Fuel-Warnung.";
    if (path.startsWith("dealer.")) return "Verhalten und UI-Timing des Händlers.";
    if (path.startsWith("light_sources.occlusion_non_player_enabled")) return "Aktiviert Wand-Blocking für externe Lichtquellen.";
    if (path.startsWith("light_sources.occlusion_apply_to_player")) return "Wendet den Occlusion-Modus auch auf den Spieler-Lichtkegel an.";
    if (path.startsWith("light_sources.occlusion_wall_bleed_ratio")) return "Wie weit Licht in angrenzende Wände hineinläuft (0 bis 1 Tilebreite).";
    if (path.startsWith("light_sources.") && path.endsWith("_radius")) return "Reichweite dieser externen Lichtquelle.";
    if (path.startsWith("light_sources.") && path.includes("core_ratio")) return "Optische Kern-/Schattenmischung dieser Lichtquelle.";
    if (path.startsWith("light_sources.lamp_lit_zone_factor")) return "Ab wann Lampen als 'sicher beleuchtet' gelten.";
    if (path.startsWith("fuel_wave.")) return "Optik/Animation der Fuel-Tank-Wellenanzeige.";
    return "Laufzeitwert für Balancing, Kamera, Licht, Audio oder UI.";
  }

  if (typeof value === "boolean") return "Aktiviert oder deaktiviert diese Option.";
  return "Feinabstimmung dieses Parameters.";
}

function setNestedValue(target: JsonRecord, path: string[], value: string | number | boolean): void {
  if (!path.length) return;
  let cursor: JsonRecord = target;
  for (let index = 0; index < path.length - 1; index += 1) {
    const key = path[index];
    const next = cursor[key];
    if (!isPlainRecord(next)) {
      const created: JsonRecord = {};
      cursor[key] = created;
      cursor = created;
      continue;
    }
    cursor = next as JsonRecord;
  }
  cursor[path[path.length - 1]] = value;
}

function numberStepForValue(value: number): string {
  if (!Number.isFinite(value)) return "1";
  if (Number.isInteger(value)) return "1";
  if (Math.abs(value) < 0.1) return "0.001";
  return "0.01";
}

function isCustomSectionKey(value: unknown): value is CustomSectionKey {
  return value === "difficulty" || value === "map_generation" || value === "runtime";
}

function difficultySourceForKey(key: DifficultyKey): Record<string, unknown> {
  const all = balanceConfig.difficulties as Record<string, unknown>;
  const source = all?.[key];
  if (isPlainRecord(source)) return source as Record<string, unknown>;
  const medium = all?.medium;
  return isPlainRecord(medium) ? (medium as Record<string, unknown>) : {};
}

function normalizeToJsonRecord(value: unknown): JsonRecord {
  if (!isPlainRecord(value)) return {};
  const out: JsonRecord = {};
  for (const [key, raw] of Object.entries(value)) {
    const normalized = normalizeToJsonValue(raw);
    if (normalized !== undefined) {
      out[key] = normalized;
    }
  }
  return out;
}

function normalizeToJsonValue(value: unknown): JsonValue | undefined {
  if (value === null) return null;
  if (typeof value === "string") return value;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return undefined;
    return value;
  }
  if (isPlainRecord(value)) return normalizeToJsonRecord(value);
  return undefined;
}

function mergeJsonRecords(base: JsonRecord, overrides: JsonRecord): JsonRecord {
  const merged: JsonRecord = {};
  for (const [key, baseValue] of Object.entries(base)) {
    merged[key] = cloneJsonValue(baseValue);
  }
  for (const [key, overrideValue] of Object.entries(overrides)) {
    const current = merged[key];
    if (isPlainRecord(current) && isPlainRecord(overrideValue)) {
      merged[key] = mergeJsonRecords(current as JsonRecord, overrideValue as JsonRecord);
      continue;
    }
    merged[key] = cloneJsonValue(overrideValue);
  }
  return merged;
}

function mergeKnownShape(base: JsonRecord, candidate: JsonRecord): JsonRecord {
  const merged: JsonRecord = {};
  for (const [key, baseValue] of Object.entries(base)) {
    const candidateValue = candidate[key];
    if (isPlainRecord(baseValue)) {
      const nestedCandidate = isPlainRecord(candidateValue) ? (candidateValue as JsonRecord) : {};
      merged[key] = mergeKnownShape(baseValue as JsonRecord, nestedCandidate);
      continue;
    }
    if (candidateValue === undefined) {
      merged[key] = cloneJsonValue(baseValue);
      continue;
    }
    if (typeof baseValue === "number") {
      const parsed = Number(candidateValue);
      merged[key] = Number.isFinite(parsed) ? parsed : baseValue;
      continue;
    }
    if (typeof baseValue === "boolean") {
      merged[key] = typeof candidateValue === "boolean" ? candidateValue : baseValue;
      continue;
    }
    if (typeof baseValue === "string") {
      merged[key] = typeof candidateValue === "string" ? candidateValue : baseValue;
      continue;
    }
    merged[key] = cloneJsonValue(baseValue);
  }
  return merged;
}

function cloneJsonValue(value: JsonValue): JsonValue {
  if (!isPlainRecord(value)) return value;
  const out: JsonRecord = {};
  for (const [key, nested] of Object.entries(value)) {
    out[key] = cloneJsonValue(nested as JsonValue);
  }
  return out;
}

function applyCustomConfigToLevel(level: LevelData, config: CustomRunConfig): LevelData {
  const clone = structuredClone(level);
  applyDifficultyConfigToLevel(clone, config.difficulty);
  const existingRuntime = normalizeToJsonRecord(clone.difficulty.runtime);
  clone.difficulty.runtime = mergeJsonRecords(existingRuntime, config.runtime);
  return clone;
}

function applyDifficultyConfigToLevel(level: LevelData, difficulty: JsonRecord): void {
  const itemSpawn = { ...(level.difficulty.itemSpawn ?? { fuel: 0, range: 0, coin: 0, sonar: 0 }) };
  for (const [key, rawValue] of Object.entries(difficulty)) {
    if (typeof rawValue !== "number" && typeof rawValue !== "string") continue;
    switch (key) {
      case "label":
        if (typeof rawValue === "string") level.difficulty.label = rawValue;
        break;
      case "num_regions":
        if (typeof rawValue === "number") level.difficulty.numRegions = Math.max(1, Math.floor(rawValue));
        break;
      case "player_speed":
        if (typeof rawValue === "number") level.difficulty.playerSpeed = rawValue;
        break;
      case "light_drain":
        if (typeof rawValue === "number") level.difficulty.lightDrain = rawValue;
        break;
      case "maze_openness":
        if (typeof rawValue === "number") level.difficulty.mazeOpenness = rawValue;
        break;
      case "fuel_spawn":
        if (typeof rawValue === "number") itemSpawn.fuel = Math.max(0, rawValue);
        break;
      case "range_spawn":
        if (typeof rawValue === "number") itemSpawn.range = Math.max(0, rawValue);
        break;
      case "coin_spawn":
        if (typeof rawValue === "number") itemSpawn.coin = Math.max(0, rawValue);
        break;
      case "sonar_spawn":
        if (typeof rawValue === "number") itemSpawn.sonar = Math.max(0, rawValue);
        break;
      default:
        break;
    }
  }
  level.difficulty.itemSpawn = itemSpawn;
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

main().catch((error) => {
  console.error(error);
  const root = document.querySelector<HTMLDivElement>("#game-root");
  if (root) {
    root.textContent = error instanceof Error ? error.message : String(error);
  }
});
